import { ARCHETYPES } from '@/components/ArchetypeSelector';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface ArchetypeCardProps {
  archetype: string | null;
}

export function ArchetypeCard({ archetype }: ArchetypeCardProps) {
  const navigate = useNavigate();
  if (!archetype) return null;

  const currentIndex = ARCHETYPES.findIndex(a => a.value === archetype);
  if (currentIndex === -1) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.08 }}
      className="card-organic p-4 cursor-pointer hover:shadow-medium transition-shadow"
      onClick={() => navigate('/settings')}
    >
      {/* Timeline of all archetypes */}
      <div className="flex items-center gap-0 w-full overflow-hidden">
        {ARCHETYPES.map((a, i) => {
          const isPast = i < currentIndex;
          const isCurrent = i === currentIndex;
          const isFuture = i > currentIndex;

          return (
            <div key={a.value} className="flex items-center flex-1 last:flex-none">
              {/* Node */}
              <div className="flex flex-col items-center gap-1.5 relative z-10">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 + i * 0.08, type: 'spring' }}
                  className={`flex items-center justify-center rounded-full transition-all ${
                    isCurrent
                      ? 'h-11 w-11 garden-gradient shadow-soft ring-2 ring-primary/20'
                      : isPast
                        ? 'h-8 w-8 bg-primary/15'
                        : 'h-8 w-8 bg-muted/80'
                  }`}
                >
                  <span className={`${isCurrent ? 'text-xl' : 'text-sm'} ${isFuture ? 'opacity-40 grayscale' : ''}`}>
                    {a.emoji}
                  </span>
                </motion.div>

                {/* Label — only show for current */}
                {isCurrent && (
                  <motion.span
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="text-[11px] font-semibold text-foreground text-center whitespace-nowrap max-w-[80px] truncate"
                  >
                    {a.label.replace('The ', '')}
                  </motion.span>
                )}
                {!isCurrent && (
                  <span className={`text-[9px] text-center whitespace-nowrap max-w-[60px] truncate ${
                    isPast ? 'text-muted-foreground' : 'text-muted-foreground/40'
                  }`}>
                    {a.label.replace('The ', '')}
                  </span>
                )}
              </div>

              {/* Connecting line (not after last) */}
              {i < ARCHETYPES.length - 1 && (
                <div className="flex-1 h-[2px] mx-1 rounded-full relative -mt-5">
                  <div className="absolute inset-0 bg-muted rounded-full" />
                  {i < currentIndex && (
                    <motion.div
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                      className="absolute inset-0 garden-gradient rounded-full origin-left"
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
